'use client'

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

// Filter button variants
export type FilterButtonVariant = 'underline' | 'pill' | 'outline' | 'minimal'

// Filter button sizes
export type FilterButtonSize = 'sm' | 'md' | 'lg'

export interface FilterButtonProps {
  label: string
  isActive?: boolean
  onClick?: () => void
  className?: string
  disabled?: boolean
  variant?: FilterButtonVariant
  size?: FilterButtonSize
}

// Variant styles
const variantStyles = {
  underline: {
    base: 'px-0 py-3 border-b border-text-primary/30 text-text-primary/60',
    active: 'border-text-primary/100  text-text-primary/100',
    hover: 'hover:text-text-primary hover:border-text-primary/100',
  },
  pill: {
    base: 'px-4 py-2 rounded-full border border-border-brand',
    active: 'bg-brand text-text-white border-border-brand',
    hover: 'hover:bg-brand hover:text-text-white',
  },
  outline: {
    base: 'px-4 py-3 border text-text-brand border-border-brand',
    active: 'bg-brand text-text-white border-border-brand',
    hover: 'hover:bg-brand hover:text-text-white',
  },
  minimal: {
    base: 'px-2 py-1',
    active: 'font-medium text-brand',
    hover: 'hover:text-brand',
  },
}

// Size styles
const sizeStyles = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
}

export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  (
    {
      label,
      isActive = false,
      onClick,
      className = '',
      disabled = false,
      variant = 'underline',
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const variantStyle = variantStyles[variant]
    const sizeStyle = sizeStyles[size]

    const buttonClasses = cn(
      'font-normal font-ui transition-all duration-300 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
      ' text-text-primary uppercase',
      sizeStyle,
      variantStyle.base,
      {
        [variantStyle.active]: isActive,
        [variantStyle.hover]: !disabled,
        'cursor-not-allowed': disabled,
        'pointer-events-none': disabled,
      },
      className,
    )

    return (
      <button ref={ref} onClick={onClick} disabled={disabled} className={buttonClasses} {...props}>
        {label}
      </button>
    )
  },
)

FilterButton.displayName = 'FilterButton'

export default FilterButton


